import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, ArrowRight, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { useTelegram } from '../hooks/useTelegram';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { setUser } from '../store/slices/authSlice';
import * as citiesApi from '../api/cities';
import * as usersApi from '../api/users';
const COUNTRY_LABELS = {
    TR: '🇹🇷 Турция',
    RU: '🇷🇺 Россия',
    OTHER: 'Другие',
};
const COUNTRY_ORDER = ['TR', 'RU', 'OTHER'];
export function CitySelectPage() {
    const navigate = useNavigate();
    const { hapticImpact } = useTelegram();
    const dispatch = useAppDispatch();
    const { user } = useAppSelector((s) => s.auth);
    const [cities, setCities] = useState([]);
    const [selected, setSelected] = useState(user?.cityId ?? null);
    const [query, setQuery] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const isProfileMode = !!user?.isProfileComplete;
    useEffect(() => {
        citiesApi.getCities().then(setCities).catch(() => { });
    }, []);
    const filtered = useMemo(() => {
        if (!query.trim())
            return cities;
        const q = query.toLowerCase();
        return cities.filter((c) => c.nameRu.toLowerCase().includes(q) ||
            c.nameEn.toLowerCase().includes(q));
    }, [cities, query]);
    const grouped = useMemo(() => {
        if (query.trim())
            return null; // flat list when searching
        const groups = {};
        for (const city of filtered) {
            const code = city.countryCode || 'OTHER';
            if (!groups[code])
                groups[code] = [];
            groups[code].push(city);
        }
        return groups;
    }, [filtered, query]);
    const handleSelect = (cityId) => {
        setSelected(cityId);
        hapticImpact('light');
    };
    const handleContinue = async () => {
        if (!selected)
            return;
        setLoading(true);
        setError('');
        try {
            const updatedUser = await usersApi.updateProfile({ cityId: selected });
            dispatch(setUser(updatedUser));
            if (isProfileMode) {
                navigate(-1);
            }
            else {
                navigate('/onboarding', { replace: true });
            }
        }
        catch {
            setLoading(false);
            setError('Не удалось сохранить город. Попробуйте ещё раз.');
        }
    };
    const renderCityButton = (city) => (_jsx("button", { onClick: () => handleSelect(city.id), className: `w-full text-left px-5 py-4 rounded-2xl text-[15px] font-medium transition-all ${selected === city.id
            ? 'bg-white text-black shadow-lg'
            : 'bg-white/[.06] text-white/55 border border-white/[.06]'}`, children: city.nameRu }, city.id));
    return (_jsxs("div", { className: "absolute inset-0 flex flex-col px-8 overflow-y-auto no-scrollbar tg-safe", style: { background: '#0A0A0A' }, children: [_jsx("div", { className: "absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] rounded-full opacity-[0.05]", style: { background: 'radial-gradient(circle, #EC4899 0%, transparent 70%)' } }), _jsxs("div", { className: "relative z-10 w-full max-w-[340px] mx-auto flex flex-col flex-1", children: [_jsx("div", { className: "pt-6", children: _jsx("button", { className: "btn-back", onClick: () => navigate(-1), children: _jsx(ArrowLeft, { className: "w-4 h-4" }) }) }), _jsxs("div", { className: "flex-1 flex flex-col pt-4", children: [_jsx(MapPin, { className: "w-8 h-8 text-pink-400 mb-6" }), _jsx("h2", { className: "text-[22px] font-bold text-white mb-1", children: isProfileMode ? 'Сменить город' : 'Где ты сейчас?' }), _jsx("p", { className: "text-white/55 text-[13px] mb-8", children: "\u041C\u044B \u043F\u043E\u043A\u0430\u0436\u0435\u043C \u043B\u044E\u0434\u0435\u0439 \u0438 \u0441\u043E\u0431\u044B\u0442\u0438\u044F \u0440\u044F\u0434\u043E\u043C" }), _jsx("div", { className: "mb-6", children: _jsx("input", { type: "text", value: query, onChange: (e) => setQuery(e.target.value), placeholder: "\u0412\u0432\u0435\u0434\u0438 \u0433\u043E\u0440\u043E\u0434", className: "w-full bg-white/[.06] border border-pink-400/40 rounded-2xl px-5 py-4 text-white text-[16px] font-medium outline-none placeholder-white/35", style: { caretColor: '#EC4899' }, autoComplete: "off" }) }), _jsx("div", { className: "mb-8 max-h-[340px] overflow-y-auto no-scrollbar", children: grouped ? (
                                // Grouped by country
                                COUNTRY_ORDER.map((code) => {
                                    const group = grouped[code];
                                    if (!group?.length)
                                        return null;
                                    return (_jsxs("div", { className: "mb-4", children: [_jsx("p", { className: "text-[12px] font-semibold text-white/40 uppercase tracking-wider mb-2 px-1", children: COUNTRY_LABELS[code] || code }), _jsx("div", { className: "space-y-2", children: group.map(renderCityButton) })] }, code));
                                })) : (
                                // Flat search results
                                _jsxs("div", { className: "space-y-2", children: [filtered.map(renderCityButton), filtered.length === 0 && query.trim() && (_jsx("p", { className: "text-white/35 text-[13px] text-center py-4", children: "\u0413\u043E\u0440\u043E\u0434 \u043D\u0435 \u043D\u0430\u0439\u0434\u0435\u043D" }))] })) }), error && (_jsx("p", { className: "text-red-400 text-[13px] mb-3 text-center", children: error })), _jsxs("div", { className: "pb-8", children: [_jsxs(Button, { onClick: handleContinue, disabled: !selected || loading, children: [isProfileMode ? 'Сохранить' : 'Продолжить', " ", _jsx(ArrowRight, { className: "w-4.5 h-4.5" })] }), !isProfileMode && (_jsx("p", { className: "text-white/45 text-[11px] text-center mt-3", children: "\u0413\u043E\u0440\u043E\u0434 \u043C\u043E\u0436\u043D\u043E \u0441\u043C\u0435\u043D\u0438\u0442\u044C \u043F\u043E\u0437\u0436\u0435 \u0432 \u043F\u0440\u043E\u0444\u0438\u043B\u0435" }))] })] })] })] }));
}
